import { useState } from 'react';
import { Layers } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Modal, ModalFooter, Button, Input, Textarea } from '@/components/ui';

interface CreateLinkPageModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: { title: string; description?: string }) => Promise<void>;
}

const CreateLinkPageModal = ({ isOpen, onClose, onSubmit }: CreateLinkPageModalProps) => {
  const { t } = useTranslation();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!title.trim()) {
      setError(t('linkPages.pageTitle') + ' is required');
      return;
    }

    setIsLoading(true);
    try {
      await onSubmit({
        title: title.trim(),
        description: description.trim() || undefined,
      });
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('linkPages.failedToCreate'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setTitle('');
    setDescription('');
    setError(null);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('linkPages.createLinkPage')}
      size="md"
    >
      <form onSubmit={handleSubmit}>
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        <div className="space-y-4">
          <Input
            label={t('linkPages.pageTitle')}
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t('linkPages.pageTitlePlaceholder')}
            leftIcon={<Layers className="w-5 h-5" />}
            autoFocus
          />

          <Textarea
            label={t('linkPages.description')}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('linkPages.descriptionPlaceholder')}
            rows={3}
          />
        </div>

        <ModalFooter>
          <Button variant="ghost" size="md" onClick={handleClose} disabled={isLoading}>
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            variant="primary"
            size="md"
            isLoading={isLoading}
            leftIcon={!isLoading ? <Layers className="w-4 h-4" /> : undefined}
          >
            {isLoading ? t('common.loading') : t('linkPages.createLinkPage')}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default CreateLinkPageModal;
